import { getLenis, isSmoothScrollEnabled } from "@theme/utilities";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)");

const clamp01 = (n) => (n < 0 ? 0 : n > 1 ? 1 : n);

const range = (p, from, to) => clamp01((p - from) / (to - from));

/** iOS momentum scrolling can skip a frame, so the loop lingers before stopping. */
const NATIVE_LINGER = 260;

const LENIS_TRIES = 30;
const LENIS_INTERVAL = 100;

class ScrollDriver {
  #writers = new Set();
  #lenis = null;
  #detach = null;
  #frame = null;
  #lastY = -1;
  #quietAt = 0;
  #tries = 0;

  constructor() {
    this.#claimLenis();
    addEventListener("resize", this.#onResize, { passive: true });
    addEventListener("orientationchange", this.#onResize, { passive: true });
    document.addEventListener("visibilitychange", this.#onVisibility);
  }

  get position() {
    return this.#lenis ? this.#lenis.scroll : scrollY;
  }

  get lenis() {
    return this.#lenis;
  }

  add(writer) {
    this.#writers.add(writer);
    writer(this.position);
    if (!this.#detach) this.#startNative();
  }

  remove(writer) {
    this.#writers.delete(writer);
  }

  /**
   * The theme imports Lenis lazily, so the first `getLenis()` returns null; retry
   * for a few seconds before settling for native scrolling.
   */
  #claimLenis = () => {
    if (this.#detach || !isSmoothScrollEnabled()) return;

    const lenis = getLenis();
    if (!lenis) {
      if (this.#tries++ < LENIS_TRIES) setTimeout(this.#claimLenis, LENIS_INTERVAL);
      return;
    }

    this.#lenis = lenis;
    this.#detach = lenis.on("scroll", (instance) => this.#flush(instance.scroll));
    this.#stopNative();
  };

  #startNative() {
    addEventListener("scroll", this.#onNativeScroll, { passive: true });
    this.#onNativeScroll();
  }

  #stopNative() {
    removeEventListener("scroll", this.#onNativeScroll);
    if (this.#frame === null) return;
    cancelAnimationFrame(this.#frame);
    this.#frame = null;
  }

  #onNativeScroll = () => {
    this.#quietAt = performance.now();
    if (this.#frame === null) this.#frame = requestAnimationFrame(this.#tick);
  };

  #tick = (now) => {
    const y = scrollY;
    if (y !== this.#lastY) {
      this.#lastY = y;
      this.#quietAt = now;
      this.#flush(y);
    }

    this.#frame = now - this.#quietAt < NATIVE_LINGER ? requestAnimationFrame(this.#tick) : null;
  };

  #flush(y) {
    for (const writer of this.#writers) writer(y);
  }

  #onResize = () => {
    // A resize can cross the breakpoint where the theme enables smooth scroll.
    this.#tries = 0;
    this.#claimLenis();
  };

  #onVisibility = () => {
    if (document.hidden || this.#detach) return;
    this.#onNativeScroll();
  };
}

let driver = null;

const getDriver = () => (driver ??= new ScrollDriver());

/**
 * The page is back at the top once smooth scrolling has started, so a link to
 * `#id` on another page would land at the top; the jump is redone after load.
 */
const revisitHash = (tries = 0) => {
  const id = decodeURIComponent(location.hash.slice(1));
  const target = id && document.getElementById(id);
  if (!target) return;

  const lenis = getLenis();
  if (!lenis && isSmoothScrollEnabled() && tries < LENIS_TRIES) {
    setTimeout(() => revisitHash(tries + 1), LENIS_INTERVAL);
    return;
  }

  if (lenis) lenis.scrollTo(target, { immediate: true, force: true });
  else target.scrollIntoView({ behavior: "instant" });
};

if (location.hash) {
  if (document.readyState === "complete") revisitHash();
  else addEventListener("load", () => revisitHash(), { once: true });
}

/**
 * Subclasses cache geometry in `measure()`; `write()` must never read layout,
 * because a read after a style write forces a relayout every frame.
 */
class ScrollTrack extends HTMLElement {
  #top = 0;
  #travel = 0;
  #last = -1;
  #subscribed = false;
  #visible = false;
  #viewport = null;
  #resizes = null;

  // Runs under reduced motion too: pinned sections still step through their states, and each
  // section's reduced-motion CSS drops the transitions or unpins its track.
  connectedCallback() {
    this.#viewport = new IntersectionObserver(
      ([entry]) => {
        this.#visible = entry.isIntersecting;
        this.#sync();
      },
      { rootMargin: "25% 0px" }
    );
    this.#viewport.observe(this);

    // Copy reflows when the web font loads, which a window resize does not catch.
    this.#resizes = new ResizeObserver(() => this.#remeasure());
    this.#resizes.observe(this);

    addEventListener("resize", this.#remeasure, { passive: true });
    this.#remeasure();
  }

  disconnectedCallback() {
    this.#visible = false;
    this.#sync();
    this.#viewport?.disconnect();
    this.#resizes?.disconnect();
    removeEventListener("resize", this.#remeasure);
  }

  #remeasure = () => {
    const rect = this.getBoundingClientRect();
    this.#top = rect.top + scrollY;
    this.#travel = rect.height - innerHeight;
    this.measure();

    this.#last = -1;
    this.#sync();
    if (this.#subscribed) this.#write(getDriver().position);
  };

  /**
   * True keeps the section driven while on screen, even after its track's
   * progress has reached 1.
   */
  get followsViewport() {
    return false;
  }

  #sync() {
    const wanted = this.#visible && (this.followsViewport || this.#travel > 0);
    if (wanted === this.#subscribed) return;

    this.#subscribed = wanted;
    if (wanted) getDriver().add(this.#write);
    else getDriver().remove(this.#write);
  }

  #write = (y) => {
    if (this.followsViewport) {
      if (y === this.#last) return;
      this.#last = y;
      this.write(clamp01((y - this.#top) / this.#travel), y);
      return;
    }

    const progress = clamp01((y - this.#top) / this.#travel);
    // A custom property write invalidates style for the whole subtree, so skip
    // frames where nothing moved.
    if (progress === this.#last) return;
    this.#last = progress;
    this.write(progress, y);
  };

  /**
   * @param {number} _progress 0 at the top of the track, 1 at the bottom.
   * @param {number} _y The raw scroll position.
   */
  write(_progress, _y) {}

  measure() {}
}

class ScrollStory extends ScrollTrack {
  write(progress) {
    const style = this.style;
    style.setProperty("--scpd-story-progress", progress.toFixed(4));
    style.setProperty("--scpd-story-centre", (1 - range(progress, 0.32, 0.52)).toFixed(3));
    style.setProperty("--scpd-story-wash", (1 - 0.72 * range(progress, 0.3, 0.62)).toFixed(3));
    style.setProperty("--scpd-story-aside", range(progress, 0.5, 0.72).toFixed(3));
    style.setProperty("--scpd-story-panel", range(progress, 0.42, 0.7).toFixed(3));
  }
}

class ScienceList extends ScrollTrack {
  #claims = [];
  #centres = [];
  #distance = 0;
  #active = null;

  measure() {
    const list = this.querySelector("[data-scpd-science-list]");
    this.#claims = list ? [...list.querySelectorAll(".scpd-science__claim")] : [];

    // The reading line is the first claim's own position, not a window fraction,
    // so the section always opens on claim one.
    this.#centres = this.#claims.map((claim) => claim.offsetTop + claim.offsetHeight / 2);

    const first = this.#centres[0] ?? 0;
    const last = this.#centres.at(-1) ?? 0;
    this.#distance = Math.max(0, last - first);
  }

  write(progress) {
    const shift = progress * this.#distance;
    const style = this.style;
    style.setProperty("--scpd-science-shift", shift.toFixed(1));
    style.setProperty("--scpd-science-progress", progress.toFixed(4));

    // Uses the cached centres; reading each claim's rect would force a layout per frame.
    const line = this.#centres[0] ?? 0;
    let best = null;
    let bestGap = Infinity;
    this.#centres.forEach((centre, index) => {
      const gap = Math.abs(centre - shift - line);
      if (gap < bestGap) {
        bestGap = gap;
        best = this.#claims[index];
      }
    });

    if (best === this.#active) return;
    this.#active?.classList.remove("is-active");
    best?.classList.add("is-active");
    this.#active = best;
  }
}

class Reveal extends HTMLElement {
  #observer = null;

  connectedCallback() {
    if (REDUCED.matches) {
      this.classList.add("is-revealed");
      return;
    }

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.classList.add("is-revealed");
        this.#observer?.disconnect();
      },
      { rootMargin: "0px 0px -12% 0px" }
    );
    this.#observer.observe(this);

    // The observer can miss an element already in view before the page settles,
    // so check once more after the first layout.
    requestAnimationFrame(() => {
      if (this.classList.contains("is-revealed")) return;
      const box = this.getBoundingClientRect();
      if (box.bottom > 0 && box.top < innerHeight * 0.88) {
        this.classList.add("is-revealed");
        this.#observer?.disconnect();
      }
    });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

export { ScrollTrack, getDriver, clamp01, range, REDUCED };

if (!customElements.get("scpd-reveal")) {
  customElements.define("scpd-reveal", Reveal);
}

if (!customElements.get("scpd-scroll-story")) {
  customElements.define("scpd-scroll-story", ScrollStory);
}

if (!customElements.get("scpd-science-list")) {
  customElements.define("scpd-science-list", ScienceList);
}
